import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import PublicNav from "@/components/PublicNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Phone, Mail, MessageCircle, ArrowRight, CheckCircle2 } from "lucide-react";

const WA_NUMBER = "919960868323";
const CONTACT_NO_DISPLAY = "+91 99608 68323";
const CONTACT_EMAIL = "Contact@INPropPortal.com";

function formatApiError(detail, fallback = "Something went wrong. Please try again.") {
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return detail?.msg || JSON.stringify(detail);
}

export default function ContactUs() {
  const [form, setForm] = useState({ name: "", contact_no: "", email: "", comment: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.contact_no.trim() || !form.comment.trim()) {
      toast.error("Please fill name, contact number and your comment.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/contact/submit", {
        name: form.name.trim(),
        contact_no: form.contact_no.trim(),
        email: form.email.trim() || undefined,
        comment: form.comment.trim(),
      });
      setDone(data);
      toast.success(data.message || "Message sent.");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-sg-paper">
      <PublicNav />

      <section className="max-w-6xl mx-auto px-6 lg:px-8 pt-14 pb-8">
        <div className="overline text-sg-gold-deep mb-3" data-testid="contact-overline">TALK TO INPROPPORTAL</div>
        <h1 className="font-display text-5xl lg:text-6xl text-sg-navy leading-[1.05]">
          We&apos;re a <span className="font-serif-italic text-sg-gold-deep">WhatsApp</span> away.
        </h1>
        <p className="mt-4 text-sg-mute max-w-xl">
          Have a question about your Pune property, our agent portal, or a partnership? Drop us a note — the team replies within a business day.
        </p>
      </section>

      <section className="max-w-6xl mx-auto px-6 lg:px-8 pb-20 grid grid-cols-1 lg:grid-cols-5 gap-8">
        {/* LEFT — direct contacts */}
        <aside className="lg:col-span-2 space-y-4">
          <a
            href={`tel:+${WA_NUMBER}`}
            className="block bg-white border border-sg-line rounded-3xl p-6 hover:border-sg-gold/60 transition-colors group"
            data-testid="contact-phone-card"
          >
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-sg-cream-soft border border-sg-line flex items-center justify-center"><Phone className="w-5 h-5 text-sg-gold-deep" /></div>
              <div>
                <div className="overline text-sg-gold-deep">CALL US</div>
                <div className="font-display text-2xl text-sg-navy mt-0.5" data-testid="contact-phone-number">{CONTACT_NO_DISPLAY}</div>
              </div>
            </div>
            <p className="text-xs text-sg-mute mt-3">Mon–Sat, 10 am – 8 pm IST.</p>
          </a>

          <a
            href={`mailto:${CONTACT_EMAIL}`}
            className="block bg-white border border-sg-line rounded-3xl p-6 hover:border-sg-gold/60 transition-colors"
            data-testid="contact-email-card"
          >
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-sg-cream-soft border border-sg-line flex items-center justify-center"><Mail className="w-5 h-5 text-sg-gold-deep" /></div>
              <div>
                <div className="overline text-sg-gold-deep">EMAIL US</div>
                <div className="font-display text-xl text-sg-navy mt-0.5 break-all" data-testid="contact-email-address">{CONTACT_EMAIL}</div>
              </div>
            </div>
            <p className="text-xs text-sg-mute mt-3">Best for partnerships, invoices, and RERA docs.</p>
          </a>

          <a
            href={`https://wa.me/${WA_NUMBER}?text=${encodeURIComponent("Hi INPropPortal, I have a question.")}`}
            target="_blank"
            rel="noopener noreferrer"
            className="block bg-[#25D366] text-white rounded-3xl p-6 hover:brightness-105 transition-all"
            data-testid="contact-whatsapp-card"
          >
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-white/15 flex items-center justify-center"><MessageCircle className="w-5 h-5" /></div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.16em] text-white/80">WHATSAPP</div>
                <div className="font-display text-xl mt-0.5">Chat on WhatsApp</div>
              </div>
            </div>
            <p className="text-xs text-white/80 mt-3">Fastest — most replies come back inside 20 minutes.</p>
          </a>
        </aside>

        {/* RIGHT — form or thank-you */}
        <div className="lg:col-span-3">
          {done ? (
            <div className="bg-white border border-sg-line rounded-3xl p-10" data-testid="contact-thankyou">
              <CheckCircle2 className="w-10 h-10 text-emerald-600" />
              <h2 className="font-display text-3xl text-sg-navy mt-4">Message received.</h2>
              <p className="text-sg-mute mt-2 max-w-lg">
                {done.message} <span className="block mt-1 text-xs text-sg-mute/70">Ref: <span className="font-mono">{done.submission_id}</span></span>
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/" className="inline-flex items-center gap-2 rounded-full bg-sg-navy hover:bg-sg-navy-700 text-white px-5 py-2.5 text-sm" data-testid="contact-thankyou-home">
                  Back to home <ArrowRight className="w-4 h-4" />
                </Link>
                <button
                  type="button"
                  onClick={() => { setDone(null); setForm({ name: "", contact_no: "", email: "", comment: "" }); }}
                  className="inline-flex items-center gap-2 rounded-full border border-sg-line px-5 py-2.5 text-sm text-sg-navy hover:bg-sg-cream-soft"
                  data-testid="contact-thankyou-again"
                >
                  Send another
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-white border border-sg-line rounded-3xl p-8 space-y-5" data-testid="contact-form">
              <div>
                <div className="overline text-sg-gold-deep">SEND US A NOTE</div>
                <h2 className="font-display text-3xl text-sg-navy mt-1">Tell us how we can help.</h2>
              </div>

              <div>
                <Label htmlFor="cf-name" className="text-xs uppercase tracking-wider text-sg-mute">Name *</Label>
                <Input
                  id="cf-name"
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Your full name"
                  className="mt-1 bg-sg-cream-soft border-sg-line rounded-full h-12"
                  data-testid="contact-form-name"
                  autoComplete="name"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="cf-phone" className="text-xs uppercase tracking-wider text-sg-mute">Contact No *</Label>
                  <Input
                    id="cf-phone"
                    value={form.contact_no}
                    onChange={set("contact_no")}
                    placeholder="+91 99608 68323"
                    className="mt-1 bg-sg-cream-soft border-sg-line rounded-full h-12"
                    data-testid="contact-form-phone"
                    autoComplete="tel"
                    inputMode="tel"
                  />
                </div>
                <div>
                  <Label htmlFor="cf-email" className="text-xs uppercase tracking-wider text-sg-mute">Email <span className="text-sg-mute/70 normal-case tracking-normal">(optional)</span></Label>
                  <Input
                    id="cf-email"
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    placeholder="you@example.com"
                    className="mt-1 bg-sg-cream-soft border-sg-line rounded-full h-12"
                    data-testid="contact-form-email"
                    autoComplete="email"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="cf-comment" className="text-xs uppercase tracking-wider text-sg-mute">Comment *</Label>
                <Textarea
                  id="cf-comment"
                  value={form.comment}
                  onChange={set("comment")}
                  rows={5}
                  placeholder="What are you looking for? A property, a partnership, or something else?"
                  className="mt-1 bg-sg-cream-soft border-sg-line rounded-2xl"
                  data-testid="contact-form-comment"
                />
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="h-12 rounded-full bg-sg-navy hover:bg-sg-navy-700 text-white px-8 disabled:opacity-60"
                data-testid="contact-form-submit"
              >
                {submitting ? "Sending…" : (<>Send message <ArrowRight className="w-4 h-4 ml-2" /></>)}
              </Button>
              <p className="text-[11px] text-sg-mute">
                By submitting, you agree we may contact you about your query. We never share your info with third-party marketers.
              </p>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
